import { useRef, useState } from 'react';
import { Card, Dropdown, Form, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart, PieChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import Flex from '@/components/common/Flex';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { getColor } from '@/helpers/utils';
import SessionByBrowserChart from './session-by-browser/SessionByBrowserChart';
import TableRow from './session-by-browser/TableRow';
import PlotSkeleton from './PlotSkeleton';

const SPAM_BAR_HEIGHT = 250;

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  PieChart,
  CanvasRenderer,
  LegendComponent
]);

const SCORE_BUCKETS = [
  ['0-10', '0–10'],
  ['11-20', '11–20'],
  ['21-30', '21–30'],
  ['31-40', '31–40'],
  ['41-50', '41–50'],
  ['51-60', '51–60'],
  ['61-70', '61–70'],
  ['71-80', '71–80'],
  ['81-90', '81–90'],
  ['91-100', '91–100']
];

const HEALTH_GROUPS = [
  { label: 'سالم', color: 'success', start: 0, end: 3 },
  { label: 'کم‌خطر', color: 'warning', start: 3, end: 6 },
  { label: 'خطرناک', color: 'danger', start: 6, end: 10 }
];

const scoreColor = (index: number) => {
  if (index < 3) return 'success';
  if (index < 6) return 'warning';
  return 'danger';
};

type SpamScoreMap = Record<string, number>;

type ScoreRow = {
  label: string;
  color: string;
  count: number;
  percentage: number;
};

type SpamScoreDistributionProps = {
  data?: { spam_score?: SpamScoreMap };
  noMore?: boolean;
  loading?: boolean;
};

const getScoreRows = (spamScore?: SpamScoreMap): ScoreRow[] => {
  const total = SCORE_BUCKETS.reduce(
    (sum, [key]) => sum + (spamScore?.[key] ?? 0),
    0
  );

  return SCORE_BUCKETS.map(([key, label], index) => {
    const count = spamScore?.[key] ?? 0;
    return {
      label,
      color: scoreColor(index),
      count,
      percentage: total ? Math.round((count * 100) / total) : 0
    };
  });
};

const getHealthRows = (spamScore?: SpamScoreMap): ScoreRow[] => {
  const scoreRows = getScoreRows(spamScore);
  const total = scoreRows.reduce((sum, row) => sum + row.count, 0);

  return HEALTH_GROUPS.map(group => {
    const count = scoreRows
      .slice(group.start, group.end)
      .reduce((sum, row) => sum + row.count, 0);
    return {
      label: group.label,
      color: group.color,
      count,
      percentage: total ? Math.round((count * 100) / total) : 0
    };
  });
};

const getBarOption = (rows: ScoreRow[]) => ({
  textStyle: { fontFamily: 'dana' },
  tooltip: {
    trigger: 'axis',
    padding: [7, 10],
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-300'),
    textStyle: { color: getColor('gray-1100') },
    borderWidth: 1,
    transitionDuration: 0
  },
  xAxis: {
    type: 'category',
    data: rows.map(row => row.label),
    axisLine: { lineStyle: { color: getColor('gray-300') } },
    axisTick: { show: false },
    axisLabel: { color: getColor('gray-400'), margin: 15 }
  },
  yAxis: {
    type: 'value',
    axisLabel: { color: getColor('gray-400'), margin: 15 },
    splitLine: {
      show: true,
      lineStyle: { color: getColor('gray-200') }
    },
    axisTick: { show: false },
    axisLine: { show: false }
  },
  series: [
    {
      type: 'bar',
      data: rows.map(row => ({
        value: row.count,
        itemStyle: {
          color: getColor(row.color),
          borderRadius: [3, 3, 0, 0]
        }
      }))
    }
  ],
  grid: { right: '3%', left: '10%', bottom: '10%', top: '5%' }
});

const SpamScoreDistribution = ({
  data,
  noMore = false,
  loading = false
}: SpamScoreDistributionProps) => {
  const [viewMode, setViewMode] = useState('score');
  const cardRef = useRef<HTMLDivElement>(null);
  const scoreRows = getScoreRows(data?.spam_score);
  const healthRows = getHealthRows(data?.spam_score);
  const rows = viewMode === 'score' ? scoreRows : healthRows;

  return (
    <Card className="h-100" ref={cardRef}>
      <FalconCardHeader
        title={
          <div className="d-flex align-items-center gap-2">
            <span>توزیع اسپم اسکور Moz دامنه‌ها</span>
            <HelpTooltip html="واحد شمارش: دامنه‌های ارجاع‌دهنده. در «بازه‌های ۱۰‌تایی اسپم»، تعداد دامنه‌ها در هر بازهٔ ۱۰ امتیازی نمایش داده می‌شود. در «تجمیع شده مشابه‌ها»، بازه‌ها در سه گروه سالم (۰ تا ۳۰)، کم‌خطر (۳۱ تا ۶۰) و خطرناک (۶۱ تا ۱۰۰) تجمیع می‌شوند." />
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
        endEl={
          <div className="d-flex align-items-center gap-2">
            <Form.Select
              size="sm"
              value={viewMode}
              onChange={event => setViewMode(event.target.value)}
              aria-label="نوع نمایش توزیع اسپم اسکور"
              style={{ width: 'auto' }}
            >
              <option value="score">بازه‌های ۱۰‌تایی اسپم</option>
              <option value="health">تجمیع شده مشابه‌ها</option>
            </Form.Select>
            {!noMore && (
              <Dropdown align="end" className="btn-reveal-trigger d-inline-block me-0">
                <Dropdown.Toggle
                  className="text-600 btn-reveal"
                  split
                  variant="reveal"
                  size="sm"
                >
                  <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
                </Dropdown.Toggle>
                <Dropdown.Menu className="border py-0">
                  <Dropdown.Item
                    onClick={() => cardRef.current?.requestFullscreen()}
                    className="link-600 fs--1 d-flex align-items-center gap-1 py-1"
                  >
                    تمام صفحه
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
        }
      />
      <Card.Body
        as={Flex}
        direction="column"
        justifyContent="between"
        className="py-0"
      >
        {loading ? (
          <PlotSkeleton height={SPAM_BAR_HEIGHT} />
        ) : viewMode === 'score' ? (
          <ReactEChartsCore
            echarts={echarts}
            option={getBarOption(scoreRows)}
            style={{ height: SPAM_BAR_HEIGHT }}
          />
        ) : (
          <div className="d-flex flex-column flex-md-row align-items-center gap-3">
            <div className="flex-grow-1 w-100">
              <SessionByBrowserChart
                data={healthRows.map(row => ({
                  label: row.label,
                  count: row.count
                }))}
              />
            </div>
            <div className="w-100" style={{ maxWidth: 320 }}>
              <Table size="sm" className="mb-0">
                <thead>
                  <tr>
                    <td className="py-3" />
                    <td className="py-3 fw-bold text-secondary text-400">
                      درصد
                    </td>
                    <td className="py-3 fw-bold text-secondary text-400 text-end">
                      تعداد دامنه
                    </td>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <TableRow key={row.label} data={row} />
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default SpamScoreDistribution;
